import { isFedrampModerate } from '@atlaskit/atlassian-context';
import FeatureGates, {
  FeatureGateEnvironment,
  PerimeterType,
} from '@atlaskit/feature-gate-js-client';

import { type FeatureFlagOverrides } from '@canvas/client-feature-flags-state';
import { BaseError, type BaseErrorOptions } from '@canvas/error';
import { type FlagClient, type FlagsFor, type RootEnv } from '@canvas/feature-flag-api';
import { shouldShowDevUI } from '@canvas/frame-api-devloop/is-dev-mode';
import { loadLocalPlatformFeatureFlags } from '@canvas/frame-api-devloop/platform-feature-flags-local';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';
import { CapabilityTier, CustomerImpact, captureException } from '@canvas/sentry-react';

import {
  FeatureFlagSource,
  type TargetApp,
  currentFeatureFlagState,
  resolvedFeatureFlagState,
  statsigClientInitalized,
} from './client-feature-flags';

export class StatsigClientInitializationError extends BaseError {
  constructor(cause: unknown, options?: BaseErrorOptions) {
    super(
      { name: 'StatsigClientInitializationError', ...options },
      `Error initializing Statsig client. Reason: ${StatsigClientInitializationError.getErrorMessage(
        cause
      )}`
    );

    this.cause = cause;
  }

  static getErrorMessage(error: unknown) {
    if (error instanceof Error) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    } else {
      return 'Unknown error type, see cause field';
    }
  }
}

function createStatSigBrowserClient(): void {
  const client: FlagClient = {
    isEnabled(flag: FlagsFor<RootEnv>): boolean {
      // `FeatureGates.checkGate` is safe to call before initialization - it
      // catches the "Client must be initialized" assertion internally and
      // returns `false`. No need to guard the call ourselves.
      return Boolean(FeatureGates.checkGate(flag));
    },
  };

  // eslint-disable-next-line no-underscore-dangle
  globalThis.__flagClient = client;
}

const environment = ((): FeatureGateEnvironment => {
  switch (process.env.VITE_CANVAS_FRONTEND_FEATURE_FLAGS_ENV) {
    case 'dev': {
      return FeatureGateEnvironment.Development;
    }
    case 'staging': {
      return FeatureGateEnvironment.Staging;
    }
    case 'prod': {
      return FeatureGateEnvironment.Production;
    }
    default: {
      return FeatureGateEnvironment.Development;
    }
  }
})();

const getPerimeter = (): PerimeterType =>
  isFedrampModerate() ? PerimeterType.FEDRAMP_MODERATE : PerimeterType.COMMERCIAL;

export const initializeStatsigFlagState = (
  featureFlagOverrides: FeatureFlagOverrides
) => {
  Object.entries(currentFeatureFlagState).forEach(([property, featureFlag]) => {
    const key = property as keyof typeof currentFeatureFlagState;

    if (
      featureFlag.source === FeatureFlagSource.Statsig &&
      featureFlag.targetApp.includes(
        process.env.VITE_CANVAS_FRONTEND_FEATURE_FLAGS_TARGET_APP as TargetApp
      )
    ) {
      const value = FeatureGates.checkGate(featureFlag.flag);
      resolvedFeatureFlagState[key].enabled = value;

      // We don't want to update the override value if it's already set
      if (featureFlagOverrides[key] === undefined) {
        currentFeatureFlagState[key].enabled = value;
      }
    }
  });
};

const IGNORABLE_NETWORK_ERROR_MESSAGES = [
  'failed to fetch', // Chrome / Edge / other Chromium browsers
  'networkerror when attempting to fetch resource', // Firefox
  'the user aborted a request.',
];

// Declaring a session data type here instead of pulling `WhiteboardSessionData` from
// `@canvas/canvas-state` to avoid potential circular dependencies b/w this
// package and canvas-state
export const initStatsig = async (
  featureFlagOverrides: FeatureFlagOverrides,
  sessionData?: {
    accountId: string;
    tenantId: string;
    orgId?: string;
  }
) => {
  const start = performance.now();
  const key = process.env.VITE_CANVAS_FRONTEND_FEATURE_FLAGS_API_KEY;
  const targetApp = process.env.VITE_CANVAS_FRONTEND_FEATURE_FLAGS_TARGET_APP;

  try {
    if (!key || !sessionData || !targetApp) {
      return;
    }

    if (process.env.MODE === 'offline' || process.env.MODE === 'test') {
      // We can't wait for Statsig in offline mode since we have no
      // network mocks for these features (would require reverse engineering their
      // API). Same thing goes for tests
      return;
    }

    let loadLocalPlatformFeatureFlagsPromise: Promise<void> | undefined = undefined;
    if (shouldShowDevUI()) {
      loadLocalPlatformFeatureFlagsPromise = loadLocalPlatformFeatureFlags(sessionData);
    }

    await FeatureGates.initialize(
      {
        apiKey: key,
        environment,
        targetApp,
        useGatewayURL: true,
        fetchTimeoutMs: 5000,
        perimeter: getPerimeter(),
      },
      {
        atlassianAccountId: sessionData.accountId,
        tenantId: sessionData.tenantId,
        atlassianOrgId: sessionData.orgId,
      }
    );

    createStatSigBrowserClient();

    const end = performance.now();

    ReliabilityMetrics.getInstance().successWithDuration(
      MetricName.STATSIG_CLIENT_INITIALIZED,
      start,
      end
    );

    if (loadLocalPlatformFeatureFlagsPromise) {
      await loadLocalPlatformFeatureFlagsPromise;
    }

    initializeStatsigFlagState(featureFlagOverrides);

    // Intentionally swallowing `TypeError: Failed to fetch` type errors as they
    // are noisy and can happen for a variety of reasons out of our control
  } catch (err) {
    const isIgnorableNetworkError =
      (err instanceof TypeError || err instanceof StatsigClientInitializationError) &&
      IGNORABLE_NETWORK_ERROR_MESSAGES.includes(err.message.toLowerCase());

    if (!isIgnorableNetworkError) {
      const end = performance.now();

      ReliabilityMetrics.getInstance().failureWithDuration(
        MetricName.STATSIG_CLIENT_INITIALIZED,
        start,
        end,
        { errorMessage: 'Failed to initialize Statsig' }
      );

      captureException(new StatsigClientInitializationError(err), {
        tags: {
          captureLocation: 'initStatsig-catch-all',
          /**
           * Users won't get our intended FF state. This could affect our rollout timelines but
           * ultimately, our default FF should always be safe for customers to be able to use.
           */
          customerImpact: CustomerImpact.ATLASSIAN,
          /**
           * This can technically have an effect on _any_ capability depending on what features we're rolling out
           * ATM so set this to C2 for now
           */
          capabilityTier: CapabilityTier.C2,
        },
      });
    }
  } finally {
    statsigClientInitalized.resolve();
  }
};
